import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Store, ShieldCheck, ArrowRight, Loader2, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, api, API, adminApi } from "@/lib/api";
import ForgotPinDialog from "@/components/ForgotPinDialog";
import AcceptInvite from "@/components/AcceptInvite";
import { LandingHero } from "@/pages/admin/LandingPage";

const LANDING_DEFAULTS = {
  title: "Hasfarm\nOrders\nPortal",
  subtitle: "Fresh flowers, ordered in seconds.",
  hero_image_url: "https://images.unsplash.com/photo-1759374517325-1bfcdd28f1e4?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
};

export default function Login() {
  const { loginStore, loginAdmin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "admin" ? "admin" : "iga");
  const [storeCode, setStoreCode] = useState("");
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const inviteToken = params.get("invite") || "";
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const [landing, setLanding] = useState(LANDING_DEFAULTS);

  useEffect(() => { if (params.get("role") === "admin") setRole("admin"); }, [params]);
  useEffect(() => { axios.get(`${API}/landing`).then((r) => setLanding({ ...LANDING_DEFAULTS, ...r.data })).catch(() => {}); }, []);

  const forgotPassword = async () => {
    if (!email.trim()) return toast.error("Enter your email first");
    try { const r = await adminApi.forgotPassword(email.trim()); toast.success(r.message); }
    catch (err) { console.error("forgot-password", err); toast.error(err.response ? formatApiErrorDetail(err.response.data?.detail) : err.message); }
  };

  const previewStore = async () => {
    setBusy(true);
    try {
      const d = await loginStore("SANDBOX", "0000");
      toast.success(`Previewing as ${d.store.store_name}`);
      navigate("/store");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (role === "iga") {
        const d = await loginStore(storeCode.trim().toUpperCase(), pin.trim());
        toast.success(`Welcome, ${d.store.store_name}`);
        navigate("/store");
      } else {
        const d = await loginAdmin(password, email.trim());
        toast.success(`Signed in as ${d?.user?.name || "admin"}`);
        navigate("/admin");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-white/25 bg-black/15 px-4 py-3.5 text-[15px] font-medium text-white placeholder:text-white/78 outline-none transition focus:border-[#EBBAD1] focus:bg-black/25";

  return (
    <div className="bg-terracotta flex min-h-screen items-center justify-center px-4 py-8">
      {inviteToken ? <AcceptInvite token={inviteToken} /> : (
      <div className="w-full max-w-md animate-fade-up">
        <div className="mb-5 flex gap-2 rounded-full bg-black/15 p-1.5">
          <button type="button" data-testid="login-role-iga-button" onClick={() => setRole("iga")}
            className={`flex flex-1 items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition ${role === "iga" ? "bg-[#EBBAD1] text-[#28282E]" : "text-white/90 hover:text-white"}`}>
            <Store className="h-4 w-4" /> IGA Store
          </button>
          <button type="button" data-testid="login-role-admin-button" onClick={() => setRole("admin")}
            className={`flex flex-1 items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition ${role === "admin" ? "bg-[#EBBAD1] text-[#28282E]" : "text-white/90 hover:text-white"}`}>
            <ShieldCheck className="h-4 w-4" /> Supplier Admin
          </button>
        </div>

        {role === "iga" ? (
          <LandingHero title={landing.title} subtitle={landing.subtitle} heroImages={landing.hero_images} heroImageUrl={landing.hero_image_url} rotateSeconds={landing.rotate_seconds} />
        ) : (
          <div className="rounded-[28px] bg-black/15 px-6 py-10 text-center">
            <span className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-[#EBBAD1] text-[#28282E]"><ShieldCheck className="h-9 w-9" strokeWidth={2.2} /></span>
            <h1 className="mt-5 font-serif text-4xl font-extrabold text-white">Supplier Admin</h1>
            <p className="mt-2 text-sm font-medium leading-relaxed text-white/90">Bulk-manage IGA store accounts, orders and production reports.</p>
          </div>
        )}

        <form onSubmit={submit} className="mt-4 space-y-3 rounded-[28px] bg-black/15 p-5">
          {role === "iga" ? (
            <>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-white/85">Store ID</label>
                <input data-testid="login-store-input" value={storeCode} onChange={(e) => setStoreCode(e.target.value)} placeholder="e.g. IGA123 or SANDBOX" required className={inputCls} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-white/85">PIN</label>
                <input data-testid="login-pin-input" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" required className={inputCls} />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-white/85">Email <span className="font-normal normal-case text-white/60">(internal users — leave blank for master password)</span></label>
                <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@hasfarm.com.au" className={inputCls} />
              </div>
              <div>
                <div className="mb-1.5 flex items-center justify-between"><label className="block text-xs font-semibold uppercase tracking-wide text-white/85">{email ? "Password or PIN" : "Admin password"}</label>
                  {email && <button type="button" data-testid="login-forgot-password-button" onClick={forgotPassword} className="text-xs font-medium text-white/85 underline decoration-dotted underline-offset-4 hover:text-[#EBBAD1]">Forgot password?</button>}</div>
                <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className={inputCls} />
              </div>
            </>
          )}
          <button type="submit" data-testid="login-submit-button" disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#28282E] py-3.5 text-base font-bold text-white shadow-lg shadow-black/20 transition hover:bg-[#1F1F24] disabled:opacity-70">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {role === "admin" ? "Sign in as admin" : "Unlock"}
            {!busy && <ArrowRight className="h-4 w-4" />}
          </button>
          {role === "admin" && (
            <button type="button" data-testid="login-preview-store-button" onClick={previewStore} disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-white/30 bg-white/10 py-3 text-sm font-bold text-white transition hover:bg-white/20 disabled:opacity-70">
              <Eye className="h-4 w-4" /> Preview store view (SANDBOX)
            </button>
          )}
          {role === "iga" && (
            <button type="button" data-testid="login-forgot-pin-button" onClick={() => setForgotOpen(true)}
              className="w-full text-center text-sm font-bold text-[#EBBAD1] underline underline-offset-4">Forgot PIN?</button>
          )}
        </form>
        <ForgotPinDialog open={forgotOpen} onClose={() => setForgotOpen(false)} initialStoreCode={storeCode} api={api} />
      </div>
      )}
    </div>
  );
}
